<template>
  <div class="panel-column panel-column--large">
    <div class="row" v-if="loaded">
      <div class="col-sm-12">
        <CompanyCard :company="owner.company" :state="owner.state" class="mb-8" />

        <template v-if="hasMissingCertificates">
          <h1 class="mt-0">{{ trans('Missing certificate') }}</h1>
          <CertificateInput
            v-for="certificate of missingCertificates"
            :key="certificate.id"
            :item="certificate"
            :loading="certificate.id === loadingCertificateId"
            @add="addCertificate"
          />
        </template>
        <div class="flex flex-col">
          <template v-if="requirementsTodo.length">
            <h4 class="mt-0">{{ trans('Requirements') }}</h4>
            <MdlProgressbar
              large
              class="mb-8"
              :progress="requirementsCompletedPercentage"
              :status="requirementsCompletedLabel"
            />
            <CourseCard
              v-for="item of requirementsTodo"
              :key="item.id"
              :item="item"
              :disabled="hasMissingCertificates"
              @launch="onLaunch"
            />
            <hr class="mdl-divider my-8" />
          </template>

          <template v-if="requirementsAdminTodo.length">
            <h4 class="mt-0">{{ trans('Tasks for your administrator') }}</h4>
            <CourseCard v-for="item of requirementsAdminTodo" :key="item.id" :item="item" @launch="onLaunch" />
            <hr class="mdl-divider my-8" />
          </template>

          <template v-if="requirementsCompleted.length">
            <h4 class="mt-0">{{ trans('Completed') }}</h4>
            <CourseCard
              v-for="item of requirementsCompleted"
              :key="item.id"
              :item="item"
              :disabled="hasMissingCertificates"
              @launch="onLaunch"
            />
            <hr class="mdl-divider my-8" />
          </template>
        </div>

        <h4 class="mt-0">{{ trans('Your access') }}</h4>
        <MdlButton primary raised @click="selectedCompany = owner.company">
          {{ trans('Manage') }}
        </MdlButton>
      </div>

      <AccessZoneModal :company="selectedCompany" @hidden="selectedCompany = null" />

      <CourseModal
        v-if="renderLaunchModal"
        :id="launcherId"
        :requirement-id="launcherRequirementId"
        :image="launcherImage"
        :title="launcherTitle"
        :identifier="launcherIdentifier"
        :context="launcherContext"
        @close="cancel"
      />
    </div>

    <div v-else>
      <MdlProgressbar indeterminate />
    </div>
  </div>
</template>

<script>
import { trans } from '@/munio/i18n'
import CourseModal from '@component/CourseModal/CourseModal.vue'
import CompanyCard from './CompanyCard.vue'
import CourseCard from './CourseCard.vue'
import AccessZoneModal from '@app/AccessZoneOverview/AccessZoneModal.vue'
import CertificateInput from './CertificateInput.vue'
import MdlButton from '@component/mdl/Button.vue'
import MdlProgressbar from '@component/mdl/ProgressBar.vue'
import { useRequirementsStore } from '@app/AccessZoneCompany/store.js'

export default {
  name: 'AccessZoneCompany',

  components: {
    CertificateInput,
    AccessZoneModal,
    CompanyCard,
    CourseCard,
    CourseModal,
    MdlButton,
    MdlProgressbar,
  },

  props: {
    id: Number,
  },

  data() {
    return {
      showInactiveZones: false,
      selectedZone: null,
      selectedCompany: null,
      loadingCertificateId: null,
      launch: null,
    }
  },

  setup() {
    const store = useRequirementsStore()

    return {
      store,
    }
  },

  async created() {
    await this.fetch()
  },

  computed: {
    isLoading() {
      return this.store.state.zones.filter((z) => z.loading).length > 0
    },

    canLaunch() {
      return this.requirements.length > 0 && !this.isLoading && !this.hasMissingCertificates
    },

    renderLaunchModal() {
      return this.canLaunch && this.launch !== null
    },

    activeZones() {
      return this.store.state.zones.filter((z) => z.user.isConnected)
    },

    registeredZones() {
      return this.store.state.zones.filter((z) => z.user.isRegistered)
    },

    inactiveZones() {
      return this.store.state.zones.filter((z) => !z.user.isConnected)
    },

    requirements() {
      return this.store.state.requirements
        .filter((r) => r.isCourse)
        .sort((a, b) => {
          if (a.object.identifier && !b.object.identifier) {
            return 1
          } else if (!a.object.identifier && b.object.identifier) {
            return -1
          }

          const aStr = (a.object.identifier ?? '') + a.object.title
          const bStr = (b.object.identifier ?? '') + b.object.title
          return aStr.localeCompare(bStr, Munio.config.i18n.language)
        })
    },

    requirementsCompleted() {
      return this.requirements.filter((r) => r.state.isValid && !r.state.expiration?.isExpiring)
    },

    requirementsCompletedPercentage() {
      return (this.requirementsCompleted.length / this.requirements.length) * 100
    },

    requirementsCompletedLabel() {
      const num = trans(':num of :total', { num: this.requirementsCompleted.length, total: this.requirements.length })
      return `${trans('Completed')}: ${num}`
    },

    requirementsTodo() {
      return this.requirements.filter((r) => !this.requirementsCompleted.includes(r) && !r.object.isAdminResponsible)
    },

    requirementsAdminTodo() {
      return this.requirements.filter((r) => !this.requirementsCompleted.includes(r) && r.object.isAdminResponsible)
    },

    missingCertificates() {
      return this.store.state.certificates.filter((c) => !c.exists)
    },

    hasMissingCertificates() {
      return this.missingCertificates.length > 0
    },

    owner() {
      return this.store.state.owner
    },

    loaded() {
      return this.store.state.loaded
    },

    launcherRequirementId() {
      return this.launch?.id
    },

    launcherId() {
      return this.launch?.object.id
    },

    launcherImage() {
      return this.launch?.object.image.header
    },

    launcherTitle() {
      return this.launch?.object.title
    },

    launcherIdentifier() {
      return this.launch?.object.identifier
    },

    launcherContext() {
      return this.id
    },
  },

  methods: {
    trans,

    async fetch() {
      const {
        data: { data },
      } = await Munio.api.currentUser.access.company(this.id)
      this.store.setZones(data.zones.map((g) => Object.assign(g, { loading: false })))
      this.store.setOwner({ company: data.company, state: data.state })
      this.store.setRequirements(data.requirements)
    },

    async addZone(zone) {
      this.selectedZone = null
      this.store.setZoneLoadingState({ id: zone.id, state: true })

      try {
        await Munio.api.currentUser.access.register(zone.company.id, zone.id)
        await this.fetch()
      } catch (e) {
        // Error handler
      }

      this.store.setZoneLoadingState({ id: zone.id, state: false })
    },

    async removeZone(zone) {
      this.store.setZoneLoadingState({ id: zone.id, state: true })

      const redirectAfterAction = this.activeZones.length === 1

      try {
        await Munio.api.currentUser.access.unregister(zone.company.id, zone.id)
      } catch (e) {
        // Error handler?
      }

      if (redirectAfterAction) {
        window.location.href = Munio.route('lms.user.access').toString()
        return
      }

      try {
        await this.fetch()
      } catch (e) {
        // Error handler?
      }

      this.store.setZoneLoadingState({ id: zone.id, state: false })
    },

    onLaunch(objectId) {
      if (this.canLaunch) {
        const requirement = this.requirements.find((requirement) => requirement.object.id === objectId)

        this.launch = requirement
      }
    },

    onZoneToggle(zone) {
      this.selectedZone = zone === this.selectedZone ? null : zone
    },

    async addCertificate(certificateType) {
      try {
        this.loadingCertificateId = certificateType.id
        await Munio.api.currentUser.certificates.add(
          certificateType.id,
          certificateType.identifier,
          certificateType.bornAt,
        )
        await this.fetch()
      } catch (err) {
        const serverError = err.response?.status === 500
        const msg = err.response?.data?.error?.message
        Munio.Flash.error(trans('Unable to add certificate'), serverError ? undefined : msg)
      } finally {
        this.loadingCertificateId = null
      }
    },

    async cancel() {
      this.launch = null
      await this.fetch()
    },
  },
}
</script>
